import { ActionIcon, Group } from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { IconUpload, IconX, IconPhoto, IconTrash } from '@tabler/icons-react'
import { type FieldInputProps, type FormikValues } from 'formik'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { Box, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { COLOR_INTENT, FONT_SIZE, SPACE } from 'Theme'

const DropzoneWrapper = styled(Dropzone)`
  cursor: pointer;
  .mantine-Dropzone-inner {
    border: 1px dashed ${COLOR_INTENT.GRAY_70};
  }
`

interface AttachmentsDropzoneProps {
  field: FieldInputProps<any>
  setFieldValue: (field: string, value: any) => void
  values: FormikValues
  maxSize?: number
  acceptTypes: string[]
  label?: string
  maxFiles?: number
  error?: string
}

export const AttachmentsDropzone: React.FC<AttachmentsDropzoneProps> = ({
  field,
  setFieldValue,
  values,
  maxSize = 5 * 1024 ** 2, // 5MB default max size
  acceptTypes,
  label,
  maxFiles = 5,
  error,
}) => {
  const handleRemoveFile = (index: number) => {
    const updatedFiles = values.attachments.filter(
      (_: File, fileIndex: number) => fileIndex !== index
    )
    setFieldValue(field.name, updatedFiles)
  }

  const handleDropFiles = (newFiles: File[]) => {
    // Check how many files are already present
    const currentFilesCount = values.attachments?.length || 0

    // Prevent adding more than the max allowed files
    if (currentFilesCount + newFiles.length > maxFiles) {
      const allowedFiles = newFiles.slice(0, maxFiles - currentFilesCount) // Only allow up to the max limit
      const updatedFiles = [...values.attachments, ...allowedFiles]
      setFieldValue(field.name, updatedFiles)
    } else {
      const updatedFiles = [...values.attachments, ...newFiles]
      setFieldValue(field.name, updatedFiles)
    }
  }
  return (
    <Box>
      {label && <Text fontSize={FONT_SIZE.PX_14}>{label}</Text>}
      <DropzoneWrapper
        onDrop={(files: File[]) => handleDropFiles(files)}
        maxSize={maxSize}
        accept={acceptTypes}
      >
        <Group
          justify="center"
          gap="xl"
          mih={220}
          style={{ pointerEvents: 'none' }}
        >
          <DropzoneWrapper.Accept>
            <IconUpload
              style={{
                width: rem(52),
                height: rem(52),
                color: 'var(--mantine-color-blue-6)',
              }}
              stroke={1.5}
            />
          </DropzoneWrapper.Accept>
          <DropzoneWrapper.Reject>
            <IconX
              style={{
                width: rem(52),
                height: rem(52),
                color: 'var(--mantine-color-red-6)',
              }}
              stroke={1.5}
            />
          </DropzoneWrapper.Reject>
          <DropzoneWrapper.Idle>
            <IconPhoto
              style={{
                width: rem(52),
                height: rem(52),
                color: 'var(--mantine-color-dimmed)',
              }}
              stroke={1.5}
            />
          </DropzoneWrapper.Idle>
        </Group>
      </DropzoneWrapper>
      {values.attachments?.length >= maxFiles && (
        <Text
          mt={SPACE.PX_4}
          as="p"
          color={COLOR_INTENT.ERROR_DARK}
          fontSize={FONT_SIZE.PX_12}
        >
          Maximum {maxFiles} files can be uploaded.
        </Text>
      )}
      {error && (
        <Text
          mt={SPACE.PX_4}
          as="p"
          color={COLOR_INTENT.ERROR_DARK}
          fontSize={FONT_SIZE.PX_12}
        >
          {error}
        </Text>
      )}
      <Text fontSize={FONT_SIZE.PX_12} color={COLOR_INTENT.GRAY_70}>
        Please upload a copy of your business license, proof of entity
        registration, or ASID Card. Drag images here or click to select files.
        Each file should not exceed 5MB.
      </Text>
      <Box>
        {values.attachments?.length > 0 &&
          values.attachments.map((attachment: File, index: number) => (
            <Flex
              key={index}
              alignItems="center"
              justifyContent="space-between"
              my={10}
            >
              <Text fontSize={14}>{attachment.name}</Text>
              <ActionIcon
                color="red"
                size={18}
                onClick={() => handleRemoveFile(index)}
                title="Remove file"
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Flex>
          ))}
      </Box>
    </Box>
  )
}
